import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { Spinner } from "./PdfViewerModal";

/**
 * Scrollable, all-pages PDF render for an already-local file (an object URL
 * from a file picker, say). Unlike the modal it does no fetching of its own,
 * so pass anything react-pdf's `file` prop accepts.
 */
const PdfInlinePreview = ({ file, className = "w-full h-60" }) => {
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const scrollRef = useRef(null);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (el) setPageWidth(Math.max(0, el.clientWidth - 16));
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div ref={scrollRef} className={`overflow-y-auto bg-gray-100 rounded ${className}`}>
      {error ? (
        <p className="p-4 text-xs text-center text-gray-500">{error}</p>
      ) : file && pageWidth > 0 ? (
        <Document
          file={file}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={(err) => setError(err?.message || "Could not render this PDF.")}
          loading={<Spinner label="Loading preview…" />}
          error={null}
        >
          {Array.from({ length: numPages }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="mx-auto mb-2 shadow-sm last:mb-0"
              loading={null}
            />
          ))}
        </Document>
      ) : (
        <Spinner label="Loading preview…" />
      )}
    </div>
  );
};

export default PdfInlinePreview;
